import {ApiException, ValidationError} from "@/domain/core/exception/exceptions";
import client_core from "@/services/clients/client_core";
import client_gateway from "@/services/clients/client_gateway";
import client_metrics from "@/services/clients/client_metrics";
import client_shortener from "@/services/clients/client_shortener";
import {setLocale} from '@/utils/i18n'
import MessageManager from "@/domain/core/utils/messageManager";
import CollaboratorScope from "@/domain/core/constant/collaboratorScope";
import AuthProviderFactory from "@/domain/login/providers/AuthProviderFactory";
import TokenRefreshManager from "@/domain/login/tokenRefreshManager";
import JwtRefreshManager from "@/domain/login/jwtRefreshManager";

const state = {
    token: null,
    jwt: null,
    remember: false,
    user: null,
    invitation: null,
    config: {},
    configFetched: {},
    pendingTokenLogin: null, // Email per cui è stato richiesto un token di login
    registrationFlow: null, // 'validation_required' or 'login_token_sent'
    authMethod: null, // 'password', 'ldap', 'social', 'email_token'
    refreshToken: null, // For providers that support token refresh (e.g., LDAP)
    activeLanguages: ['en', 'it']
}

   
function isJwtExpired(token) {
    if (!token || token === 'undefined') return true
    const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
    return payload.exp < Date.now() / 1000
}


export const getters = {
    authProviderFactory: () => AuthProviderFactory,
    isAuthenticated: state => !!state.token,
    invitation: state => state.invitation,
    pendingTokenLogin: state => state.pendingTokenLogin,
    registrationFlow: state => state.registrationFlow,
    email: state => state.user?.email,
    isConfigFetched: (state) => (type,id) => {
        if (!state.configFetched[type]) return false
        return String(state.configFetched[type]['id']) === String(id)
    },
    hasPerm: (state) => (perm) => {
        if (!state.user || !state.user.perms) return false
        if (state.config && perm in state.config && state.config[perm]) return true
        return state.user.perms.includes(perm)
    },
    hasPerms: (state) => (perms) => {
        if (!state.user || !state.user.perms) return false
        for (const perm of perms) {
            if (state.config && perm in state.config && state.config[perm]) continue
            if (!state.user.perms.includes(perm))
                return false
        }
        return true
    },
    hasAnyPerms: (state) => (perms) => {
        if (!state.user || !state.user.perms) return false
        if (typeof perms === 'string') perms = [perms]

        for (const perm of perms) {
            if (state.config && perm in state.config && state.config[perm])
                return true
            if (state.user.perms.includes(perm))
                return true
        }
        return false
    },
    userCompactName: (state) => {
        if (!state.user) return ''
        if (!state.user.first_name || !state.user.last_name) return state.user.email
        return `${state.user.first_name[0]}. ${state.user.last_name}`
    },
    userFullName: (state) => {
        if (!state.user) return ''
        return `${state.user.first_name} ${state.user.last_name}`
    },
    userIdeogramName: (state) => {
        if (!state.user) return ''
        if (state.user.first_name) {
            return `${state.user.first_name[0]}`.toLowerCase()
        } else if (state.user.email) {
            return `${state.user.email[0]}`.toLowerCase()
        }
    },
    isInGroup: (state) => (group) => state.user?.groups.indexOf(group) !== -1,
    isPicaManager: (state) => {
        return state.user?.groups.includes('pica-managers')
    },
    photoService: (state) => {
        return state.user.photo_service || null
    },
    user: state => state.user,
    userTimeZone: (state) => {
        if (!state.user) return 'UTC'
        return state.user.timezone
    },
    jwt: state => state.jwt,
    token: state => state.token,
    userLanguage: (state, getters) => {
        if (!state.user) {
            const browserLanguage = navigator.language.includes('-') ? navigator.language.split('-')[0] : navigator.language
            if (getters.activeLanguages.includes(browserLanguage))
                return browserLanguage
            else
                return 'en'

        }
        return state.user.language
    },
    activeLanguages: state => state.activeLanguages
}

export const mutations = {
    remember(state, remember) {
        state.remember = remember
        if (remember) localStorage.setItem('remember', remember)
        else localStorage.removeItem('remember')
    },
    setAuthMethod(state, method) {
        state.authMethod = method
        if (method && state.remember) {
            localStorage.setItem('authMethod', method)
        } else {
            localStorage.removeItem('authMethod')
        }
    },
    setRefreshToken(state, refreshToken) {
        state.refreshToken = refreshToken
        if (refreshToken && state.remember) {
            localStorage.setItem('refreshToken', refreshToken)
        } else {
            localStorage.removeItem('refreshToken')
        }
    },
    setToken(state, token) {
        state.token = token
        if (token === null) {
            if (state.remember)
                localStorage.removeItem('token')
            delete client_core.defaults.headers.common['Authorization']
            delete client_gateway.defaults.headers.common['Authorization']
        } else {
            if (state.remember)
                localStorage.setItem('token', JSON.stringify(token))
            client_core.defaults.headers.common['Authorization'] = `${token.type} ${token.token}`
            client_gateway.defaults.headers.common['Authorization'] = `${token.type} ${token.token}`
        }
    },
    setJwt(state, jwt) {
        state.jwt = jwt
        if (jwt === null) {
            if (state.remember)
                localStorage.removeItem('jwt')
            delete client_metrics.defaults.headers.common['Authorization']
            delete client_shortener.defaults.headers.common['Authorization']

        } else {
            if (state.remember)
                localStorage.setItem('jwt', jwt)
            client_metrics.defaults.headers.common['Authorization'] = `Bearer ${jwt}`
            client_shortener.defaults.headers.common['Authorization'] = `Bearer ${jwt}`
        }
    },
    setUser(state, user) {
        if (user === null) {
            state.user = null
            localStorage.removeItem('user')
            // Reset MessageManager userId to anonymous
            MessageManager.setParams({userId: 'anonymous'})
            return
        }

        state.user = user
        if (state.remember)
            localStorage.setItem('user', JSON.stringify(user))
        setLocale(user.language, user.timezone)
        
        // Update MessageManager with the new userId for MQTT subscriptions
        MessageManager.setParams({userId: user.id})
    },
    logout(state) {
        state.token = null
        state.jwt = null
        state.user = null
        state.remember = false
        state.config = false
        state.authMethod = null
        state.refreshToken = null
        localStorage.removeItem('token')
        localStorage.removeItem('jwt')
        localStorage.removeItem('user')
        localStorage.removeItem('remember')
        localStorage.removeItem('authMethod')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('configFetched')
        localStorage.removeItem('tours')

        delete client_core.defaults.headers.common['Authorization']
        delete client_gateway.defaults.headers.common['Authorization']
    },
    invitation(state, invitation) {
        state.invitation = invitation
    },
    setPendingTokenLogin(state, email) {
        state.pendingTokenLogin = email
    },
    setRegistrationFlow(state, flow) {
        state.registrationFlow = flow
    },
    config(state, {config, type, id, refreshable = true, scope = null}) {
        if (!type) {
            state.configFetched = {}
            state.config = {}
            return;
        }

        state.configFetched[type] = {
            id: id,
            config: config,
            refreshable: refreshable,
            scope: scope
        }

        state.config = {}
        for (const type in state.configFetched)
            Object.assign(state.config, state.configFetched[type].config)

        if (state.remember)
            localStorage.setItem('configFetched', JSON.stringify(state.configFetched))
    }
}

export const actions = service => {
    // Initialize AuthProviderFactory with service
    AuthProviderFactory.init(service)

    return {
    // eslint-disable-next-line no-unused-vars
    async createUser({commit}, {email}) {
        try {
            await service.createUser(email)
            // If successful, user needs to validate registration code
            commit('setRegistrationFlow', 'validation_required')
            commit('setPendingTokenLogin', email)
        } catch (e) {
            if (e instanceof ValidationError) {
                const errorCode = e.getCode ? e.getCode() : e.data?.code
                
                if (errorCode === 'user_require_validation') {
                    // New user registered, needs validation
                    commit('setRegistrationFlow', 'validation_required')
                    commit('setPendingTokenLogin', email)
                } else if (errorCode === 'user_already_registered') {
                    // Existing user, login token sent
                    commit('setRegistrationFlow', 'login_token_sent')
                    commit('setPendingTokenLogin', email)
                } else {
                    // Other validation error, re-throw
                    throw e
                }
            } else {
                // Other error types, re-throw
                throw e
            }
        }
    },
    async authenticate({commit, dispatch}, {method, credentials, remember}) {
        if (remember !== undefined)
            commit('remember', remember)

        try {
            // Get the appropriate provider
            const provider = AuthProviderFactory.getProvider(method)

            // Authenticate using the provider
            const result = await provider.authenticate(credentials)

            // Store authentication data
            commit('setToken', {token: result.token, type: result.type})
            commit('setJwt', result.jwt)
            commit('setAuthMethod', method)

            // Store refresh token if available (e.g., LDAP)
            if (result.refresh_token) {
                commit('setRefreshToken', result.refresh_token)
            }
        } catch (e) {
            if (e instanceof ApiException)
                commit('logout')
            throw e
        }

        await dispatch('getCurrentUser')
    },
    // Legacy method for backward compatibility - delegates to unified authenticate
    async authenticatePassword({dispatch}, {email, password, allowRegister, remember}) {
        return await dispatch('authenticate', {
            method: 'password',
            credentials: {email, password, allowRegister},
            remember
        })
    },
    async getCurrentUser({commit, dispatch}) {
        commit('setUser', await service.getUserData())
        commit('config', {config: null, type: null})
        // Reset advanced flags on login
        localStorage.removeItem('wizard_advanced')

        // Clear pending token login and registration flow after successful login
        commit('setPendingTokenLogin', null)
        commit('setRegistrationFlow', null)

        await dispatch('fetchConfig', {type:'globals'})
        dispatch('userStatus/fetchStatus', null, {root: true})

        // Start token refresh monitoring for providers that support it
        TokenRefreshManager.start()
        JwtRefreshManager.start()
    },
    async socialLogin({commit, dispatch, getters}, data) {
        commit('remember', true)
        try {
            data['language'] = getters['userLanguage']
            const result = await service.convertAuthToken(data)
            commit('setToken', {token: result.access_token, type: result.token_type})
            await dispatch('refreshJwt')
            await dispatch('getCurrentUser')
        } catch (e) {
            if (e instanceof ApiException)
                commit('logout')
            throw e
        }
    },
    async logout({state, commit}) {
        // Stop token refresh monitoring
        TokenRefreshManager.stop()
        JwtRefreshManager.stop()

        // Call provider-specific logout if auth method is known
        if (state.authMethod) {
            try {
                const provider = AuthProviderFactory.getProvider(state.authMethod)
                await provider.logout({
                    token: state.token,
                    refresh_token: state.refreshToken
                })
            } catch (e) {
                // Log error but continue with local logout
                console.warn('Provider logout failed:', e)
            }
        }

        commit('logout')
        commit('core/resetCache', null, {root: true})
        commit('events/resetState', null, {root: true})
    },
    async changePassword({getters, dispatch}, params) {
        await service.changePassword(params.password, params.passwordRepeat)
        const email = getters['email']
        dispatch('logout')
        await dispatch('authenticate', {email, password: params.password})
    },
    async refreshJwt({getters, commit}, { force = false } = {}) {
        if (!getters['user']) return
        if (!force && !isJwtExpired(getters['jwt'])) return
        commit('setJwt', await service.getJwt())
    },
    async refreshAuthToken({state, commit}) {
        if (!state.authMethod) {
            throw new Error('No authentication method set')
        }

        const provider = AuthProviderFactory.getProvider(state.authMethod)

        if (!provider.supportsRefresh()) {
            throw new Error(`Provider ${state.authMethod} does not support token refresh`)
        }

        const result = await provider.refreshToken({
            token: state.token,
            refresh_token: state.refreshToken
        })

        // Update tokens with new values (including rotated refresh token)
        commit('setToken', {token: result.token, type: result.type})
        commit('setJwt', result.jwt)

        if (result.refresh_token) {
            commit('setRefreshToken', result.refresh_token)
        }

        return result
    },
    async changeUserData({dispatch}, params) {
        await service.changeUserData({
            first_name: params.first_name,
            last_name: params.last_name,
            language: params.language,
            timezone: params.timezone
        })
        await dispatch('getCurrentUser')
    },
    async previewInvitation({commit}, {code, email}) {
        const preview = await service.previewInvitation(code, email)
        commit('invitation', preview)
        return true
    },
    async fetchConfig({commit, getters}, {type, id, forceRefresh = false, scope = null}) {
        if (id === undefined) id = 0

        // Se non forziamo il refresh e il config è già stato caricato, esci
        if (!forceRefresh && getters['isConfigFetched'](type, id)) {
            return
        }

        // Carica il nuovo config
        const newConfig = await service.getConfig(type, id)

        // Aggiorna lo state con il nuovo config
        commit('config', {
            config: newConfig,
            type: type,
            id: id,
            scope: scope
        })
    },
    setPermissions({commit}, {type, permissions, id, scope = null}) {
        if (id === undefined) id = 0
        commit('config', {
            config: permissions,
            type: type,
            id: id,
            scope: scope
        })
    },
    clearScopedConfigs({state, commit}, scope) {
        const remaining = {}
        for (const type in state.configFetched) {
            if (state.configFetched[type].scope !== scope) {
                remaining[type] = state.configFetched[type]
            }
        }

        // Reset e ricostruisci
        state.configFetched = remaining
        state.config = {}
        for (const type in state.configFetched)
            Object.assign(state.config, state.configFetched[type].config)

        if (state.remember)
            localStorage.setItem('configFetched', JSON.stringify(state.configFetched))
    },
    async acceptInvitation({commit}, {code, email}) {
        const response = await service.acceptInvitation(code, email)

        // NON pulire l'invito qui - lo faremo dopo il redirect riuscito
        // commit('invitation', null)

        // Con il nuovo sistema passwordless, se l'utente non è autenticato
        // il backend invia automaticamente un token di login via email
        if (response.login_token_sent) {
            // Segnala che stiamo aspettando un token per questa email
            commit('setPendingTokenLogin', email)
            return true
        }
        return false
    },
    async redirectAfterAccept({state, commit, getters, dispatch}, router) {
        // 1. Prima controlla se c'è un invito da code in sessionStorage (salvato da Accept.vue)
        // Questo ha priorità perché significa che l'utente è arrivato da un link email
        const pendingCodeInvitation = sessionStorage.getItem('pendingInvitationCode')
        if (pendingCodeInvitation) {
            try {
                const parsed = JSON.parse(pendingCodeInvitation)
                if (parsed && parsed.code) {
                    // Redirect alla pagina invitations con il code
                    const redirectRoute = {
                        name: 'invitations.list',
                        query: { code: parsed.code }
                    }
                    if (router) {
                        await router.push(redirectRoute)
                    }
                    return redirectRoute
                }
            } catch (e) {
                // Se il parsing fallisce, rimuovi e continua
                sessionStorage.removeItem('pendingInvitationCode')
            }
        }

        // 2. Controlla se è un flusso SAAS (utente arrivato da business.getpica.com)
        const pendingSaas = sessionStorage.getItem('pendingSaasActivation')
        if (pendingSaas) {
            try {
                // Chiama endpoint per diventare SAAS (crea ContractManager)
                await service.becomeSaas()
                // Aggiorna lo user status dopo becomeSaas perché il fetchStatus iniziale
                // è avvenuto prima e non riflette ancora il nuovo stato SAAS
                await dispatch('userStatus/fetchStatus', null, {root: true})
            } catch (e) {
                // Se fallisce (es. già SAAS), non è critico - continua comunque
                console.warn('becomeSaas failed:', e)
            } finally {
                // Pulisci sempre il flag
                sessionStorage.removeItem('pendingSaasActivation')
            }

            // Redirect alla lista eventi (i parametri region/plan/category sono già nello store events)
            const redirectRoute = { name: 'eventList' }
            if (router) {
                await router.push(redirectRoute)
            }
            return redirectRoute
        }

        // 3. Se non c'è un invito pendente, vai alla lista eventi di default
        if (!state.invitation) {
            if (router) {
                await router.push({name: 'eventList'})
            }
            return {name: 'eventList'}
        }

        const invitation = state.invitation
        let redirectRoute = null

        // Determina la route di destinazione basata sul tipo di invito
        if (invitation.scope_type === CollaboratorScope.PICA_SERVICE && invitation.pica_service) {
            // Verifica se l'utente ha i permessi per accedere al servizio
            // Prima dobbiamo caricare i permessi del servizio
            await dispatch('fetchConfig', {
                type: 'pica_service',
                id: invitation.pica_service.id
            })

            // Verifica se ha almeno un permesso di visualizzazione
            if (getters['hasAnyPerms'](['pica_service.view', 'pica_service.admin'])) {
                redirectRoute = {
                    name: 'eventIndex',
                    params: {picaServiceId: invitation.pica_service.id}
                }
            }
        } else if (invitation.scope_type === CollaboratorScope.CONTRACT_MANAGER && invitation.contract_manager) {
            // Verifica se l'utente ha i permessi per accedere al contract manager
            await dispatch('fetchConfig', {
                type: 'contract_manager',
                id: invitation.contract_manager.id
            })

            if (getters['hasAnyPerms'](['contract_manager.view', 'contract_manager.admin'])) {
                redirectRoute = {
                    name: 'contract_manager.detail',
                    params: {id: invitation.contract_manager.id}
                }
            }
        } else if (invitation.scope_type === CollaboratorScope.GLOBAL) {
            // Per inviti globali, vai alla lista eventi
            redirectRoute = {name: 'eventList'}
        }

        // Se non abbiamo trovato una route valida con permessi, vai alla lista eventi
        if (!redirectRoute) {
            redirectRoute = {name: 'eventList'}
        }

        // Pulisci l'invito dopo aver determinato il redirect
        commit('invitation', null)

        // Esegui il redirect se il router è fornito
        if (router) {
            await router.push(redirectRoute)
        }

        return redirectRoute
    },
    async rememberFromStorage({commit, dispatch, state}) {
        commit('remember', localStorage.getItem('remember') || false)
        const version = localStorage.getItem('version') || null
        // If the data was saved with a different version, don't load it
        if (process.env.VUE_APP_VERSION !== version) {
            localStorage.clear()
            sessionStorage.clear()
            localStorage.setItem('version', process.env.VUE_APP_VERSION)
            return
        }
        commit('invitation', JSON.parse(sessionStorage.getItem('invitation')) || null)
        commit('setToken', JSON.parse(localStorage.getItem('token')) || null)
        commit('setJwt', localStorage.getItem('jwt') || null)
        commit('setUser', JSON.parse(localStorage.getItem('user')) || null)
        commit('setAuthMethod', localStorage.getItem('authMethod') || null)
        commit('setRefreshToken', localStorage.getItem('refreshToken') || null)

        const configs = JSON.parse(localStorage.getItem('configFetched') || '{}')
        if (Object.keys(configs).length === 0)
            commit('logout')

        for (const type in configs) {
            const config = configs[type]
            commit('config', {config: config.config, type: type, id: config.id, refreshable: config.refreshable, scope: config.scope || null})
        }
        dispatch('refreshJwt')

        // Restart token refresh monitoring after page reload
        if (state.user && state.authMethod) {
            TokenRefreshManager.start()
            JwtRefreshManager.start()
        }

        // Refresh user status (certification pending, etc.) after page reload
        if (state.user) {
            dispatch('userStatus/fetchStatus', null, {root: true})
        }
    },
    // eslint-disable-next-line no-unused-vars
    async resendValidationCode({getters}, {email}) {
        await service.resendValidationCode(email)
    },
    async validateCode({commit, dispatch}, {email, code}) {
        const result = await service.validateCode(email, code)
        commit('remember', true)
        commit('setToken', {token: result.token, type: 'Token'})
        commit('setJwt', result.jwt)
        // Pulisci il registration flow dopo validazione riuscita
        commit('setPendingTokenLogin', null)
        commit('setRegistrationFlow', null)
        await dispatch('getCurrentUser')
    },
    async sendLoginToken({commit}, {email}) {
        const response = await service.sendLoginToken(email)
        // Segnala che stiamo aspettando un token per questa email
        commit('setPendingTokenLogin', email)
        return response
    },
    async authenticateWithToken({commit, dispatch}, {email, token}) {
        const result = await service.loginWithToken(email, token)
        commit('remember', true)
        commit('setToken', {token: result.token, type: 'Token'})
        commit('setJwt', result.jwt)
        // Pulisci il pending token login e registration flow dopo autenticazione riuscita
        commit('setPendingTokenLogin', null)
        commit('setRegistrationFlow', null)
        await dispatch('getCurrentUser')
    },
    async refreshPermissions({state, dispatch, commit}) {
        if (!state.user) return

        // Ricarica user data
        const user = await service.getUserData()
        commit('setUser', user)

        // Ricarica tutti i config attualmente in cache forzando il refresh
        // Questo aggiorna i permessi per ogni contesto (global, contract-manager, pica-service)
        const configsToRefresh = {...state.configFetched}

        // Ricarica tutti i config in parallelo per maggiore efficienza
        const refreshPromises = []
        for (const type in configsToRefresh) {
            const {id, refreshable} = configsToRefresh[type]

            // I config non refreshable (es. gallery) vengono cancellati
            // Si auto-sistemeranno al reload della pagina quando l'utente naviga nel contesto
            if (refreshable === false) {
                delete state.configFetched[type]
                continue
            }

            refreshPromises.push(
                dispatch('fetchConfig', {type, id, forceRefresh: true})
            )
        }

        // Ricalcola state.config dopo aver rimosso i non-refreshable
        state.config = {}
        for (const type in state.configFetched)
            Object.assign(state.config, state.configFetched[type].config)

        if (state.remember)
            localStorage.setItem('configFetched', JSON.stringify(state.configFetched))

        // Aspetta che tutti i config siano ricaricati
        await Promise.all(refreshPromises)
    }
}}

export default loginService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(loginService)
})

